import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { Inbox, LayoutDashboard, Ticket, Settings as SettingsIcon, LogOut, ChevronsLeft, ChevronsRight } from "lucide-react";
import { WS_BASE, playPing, api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SocketContext = createContext(null);
export const useSocket = () => useContext(SocketContext);

export default function Layout() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [brownie, setBrownie] = useState(null);
  const [availability, setAvailability] = useState(user?.availability || "online");
  const wsRef = useRef(null);
  const listeners = useRef(new Set());
  const loc = useLocation();

  const subscribe = useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  const sendWs = useCallback((data) => {
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(data));
  }, []);

  const refreshUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations", { params: { filter: "open" } });
      setUnreadTotal(data.reduce((s, c) => s + (c.unread_agent || 0), 0));
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    let stop = false;
    const connect = () => {
      const t = localStorage.getItem("sd_token");
      if (!t || stop) return;
      const ws = new WebSocket(`${WS_BASE}/ws/agent?token=${t}`);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        const data = JSON.parse(ev.data);
        if (data.event === "message" && data.message?.sender === "customer") {
          playPing();
          setUnreadTotal((n) => n + 1);
        }
        listeners.current.forEach((fn) => fn(data));
      };
      ws.onclose = () => { if (!stop) setTimeout(connect, 3000); };
    };
    connect();
    refreshUnread();
    return () => { stop = true; wsRef.current?.close(); };
  }, [refreshUnread]);

  useEffect(() => {
    document.title = unreadTotal > 0 ? `(${unreadTotal}) Support Desk` : "Support Desk";
  }, [unreadTotal]);

  useEffect(() => {
    const refresh = () => {
      api.get("/me/brownie").then((r) => setBrownie(r.data)).catch(() => {});
      api.get("/auth/me").then((r) => setAvailability(r.data.availability || "online")).catch(() => {});
    };
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const hb = setInterval(() => sendWs({ event: "heartbeat" }), 240000);
    return () => clearInterval(hb);
  }, [sendWs]);

  const toggleAvailability = async () => {
    const next = availability === "online" ? "offline" : "online";
    setAvailability(next);
    try { await api.patch("/me/availability", { availability: next }); } catch (e) { setAvailability(availability); }
  };

  const nav = [
    { to: "/", label: "Inbox", icon: Inbox, badge: unreadTotal },
    { to: "/tickets", label: "Tickets", icon: Ticket },
    ...(user?.role === "admin" ? [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard }, { to: "/settings", label: "Settings", icon: SettingsIcon }] : []),
  ];

  return (
    <SocketContext.Provider value={{ subscribe, sendWs, refreshUnread, setUnreadTotal }}>
      <div className="sd-app flex flex-col h-screen overflow-hidden">
        <header className="flex items-center justify-between px-4 py-2.5 border-b-2 border-[#131E3A] shrink-0" style={{ background: "var(--sd-cream)" }}>
          <div className="flex items-center gap-3">
            <h1 className="font-heading font-extrabold text-xl tracking-tight">Support Desk</h1>
            <span className="font-script text-lg text-amber-800 hidden sm:inline">live chat, handled ✦</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="availability-toggle" onClick={toggleAvailability}
              className="btn-outline-tactile px-3 py-1 text-xs flex items-center gap-1.5"
              title={availability === "online" ? "You're online — new chats can be assigned to you. Click to go on break." : "You're offline (on break) — no new chats will be assigned. Click to go online."}>
              <span className={`w-2 h-2 rounded-full ${availability === "online" ? "bg-green-500" : "bg-slate-400"}`} />
              {availability === "online" ? "Online" : "Offline"}
            </button>
            <a data-testid="crm-link" href="https://crm.gosnd.com" target="_blank" rel="noreferrer" className="btn-outline-tactile px-3 py-1 text-xs">SND CRM ↗</a>
            <span className="text-xs font-bold font-heading hidden sm:inline">{user?.name} · <span className="uppercase text-[10px] tracking-wider bg-[#FDF0A0] border border-[#131E3A] rounded px-1.5 py-0.5">{user?.role}</span></span>
            <button data-testid="logout-button" onClick={logout} className="btn-outline-tactile p-1.5" title="Logout"><LogOut size={14} /></button>
          </div>
        </header>
        <div className="flex flex-1 overflow-hidden">
          <aside className={`${collapsed ? "w-14" : "w-44"} shrink-0 border-r-2 border-[#131E3A] flex flex-col py-3 px-2 gap-1 transition-all duration-200`}>
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `flex items-center gap-2.5 px-2.5 py-2 rounded-lg font-heading font-semibold text-sm transition-colors ${isActive ? "bg-[#131E3A] text-white" : "hover:bg-[#FDF0A0]"}`}>
                <n.icon size={17} className="shrink-0" />
                {!collapsed && <span className="flex-1">{n.label}</span>}
                {!collapsed && n.badge > 0 && <span data-testid="inbox-unread-badge" className="bg-[#FFD1DC] border border-[#131E3A] text-[10px] font-bold rounded-full px-1.5">{n.badge}</span>}
              </NavLink>
            ))}
            <div className="flex-1" />
            {brownie && (collapsed ? (
              <div data-testid="my-brownie-compact" className={`text-center font-heading font-extrabold text-sm py-1 ${brownie.points < 0 ? "text-red-600" : ""}`} title={`My brownie points: ${brownie.points}`}>{brownie.points > 0 ? `+${brownie.points}` : brownie.points}</div>
            ) : (
              <div data-testid="my-brownie-card" className="card-sticky p-2.5 mb-1 text-center">
                <p className="text-[9px] font-bold uppercase tracking-widest">My Brownie Points</p>
                <p className={`font-heading font-extrabold text-2xl ${brownie.points < 0 ? "text-red-600" : ""}`}>{brownie.points > 0 ? `+${brownie.points}` : brownie.points}</p>
                <p className="text-[10px] text-slate-600">{brownie.ratings_count} customer rating{brownie.ratings_count === 1 ? "" : "s"}</p>
              </div>
            ))}
            <button data-testid="sidebar-collapse-button" onClick={() => setCollapsed(!collapsed)} className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-[#FDF0A0] text-sm font-semibold">
              {collapsed ? <ChevronsRight size={16} /> : <><ChevronsLeft size={16} /> Collapse</>}
            </button>
          </aside>
          <main className="flex-1 overflow-hidden" key={loc.pathname}>
            <Outlet />
          </main>
        </div>
      </div>
    </SocketContext.Provider>
  );
}
